/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import {
  GoogleGenAI,
  LiveCallbacks,
  LiveClientToolResponse,
  LiveConnectConfig,
  LiveServerContent,
  LiveServerMessage,
  LiveServerToolCall,
  LiveServerToolCallCancellation,
  Part,
  Session,
} from '@google/genai';
import EventEmitter from 'eventemitter3';
import { difference } from 'lodash';
import { base64ToArrayBuffer } from './utils';

/**
 * Represents a single log entry in the system.
 * Used for tracking and displaying system events, messages, and errors.
 */
export interface StreamingLog {
  // Optional count for repeated log entries
  count?: number;
  // Optional additional data associated with the log
  data?: unknown;
  // Timestamp of when the log was created
  date: Date;
  // The log message content
  message: string | object;
  // The type/category of the log entry
  type: string;
}

/**
 * Event types that can be emitted by the MultimodalLiveClient.
 * Each event corresponds to a specific message from GenAI or client state change.
 */
export interface LiveClientEventTypes {
  // Emitted when audio data is received
  audio: (data: ArrayBuffer) => void;
  // Emitted when the connection closes
  close: (event: CloseEvent) => void;
  // Emitted when content is received from the server
  content: (data: LiveServerContent) => void;
  // Emitted when an error occurs
  error: (e: ErrorEvent) => void;
  // Emitted when the server interrupts the current generation
  interrupted: () => void;
  // Emitted for logging events
  log: (log: StreamingLog) => void;
  // Emitted when the connection opens
  open: () => void;
  // Emitted when the initial setup is complete
  setupcomplete: () => void;
  // Emitted when a tool call is received
  toolcall: (toolCall: LiveServerToolCall) => void;
  // Emitted when a tool call is cancelled
  toolcallcancellation: (
    toolcallCancellation: LiveServerToolCallCancellation
  ) => void;
  // Emitted when the current turn is complete
  turncomplete: () => void;
  inputTranscription: (text: string, isFinal: boolean) => void;
  outputTranscription: (text: string, isFinal: boolean) => void;
}

// FIX: Refactored to use composition over inheritance for EventEmitter
export class GenAILiveClient {
  // FIX: Use an internal EventEmitter instance
  private emitter = new EventEmitter<LiveClientEventTypes>();

  // FIX: Expose on/off methods
  public on = this.emitter.on.bind(this.emitter);
  public off = this.emitter.off.bind(this.emitter);

  protected readonly client: GoogleGenAI;
  protected session?: Session;

  // Stricter state machine to prevent race conditions
  private _status: 'connecting' | 'open' | 'closing' | 'closed' = 'closed';
  public get status() {
    return this._status;
  }

  // Expose the underlying GoogleGenAI client for direct API access
  public get ai() {
    return this.client;
  }

  /**
   * Creates a new GenAILiveClient instance.
   * @param apiKey - API key for authentication with Google GenAI
   */
  constructor(apiKey: string) {
    this.client = new GoogleGenAI({
      apiKey: apiKey,
    });
  }

  public async connect(config: LiveConnectConfig): Promise<boolean> {
    if (this._status === 'open' || this._status === 'connecting') {
      console.warn('Connection attempt ignored, already connecting or open.');
      return false;
    }

    this._status = 'connecting';
    this.log('client.connect', 'Connecting...');
    const callbacks: LiveCallbacks = {
      onopen: this.onOpen.bind(this),
      onmessage: this.onMessage.bind(this),
      onerror: this.onError.bind(this),
      onclose: this.onClose.bind(this),
    };

    try {
      // Use the full configuration passed from the component
      console.log('Connecting with config:', config);
      
      this.session = await this.client.live.connect({
        model: 'gemini-2.5-flash-native-audio',
        config: config,
        callbacks,
      });

    } catch (e: any) {
      console.error('Connection error:', e);
      
      // Handle specific "invalid argument" errors by trying with minimal config
      if (e.message && e.message.includes('invalid argument')) {
        console.log('Trying with minimal configuration...');
        try {
          const minimalConfig = {
            responseModalities: ['AUDIO', 'TEXT'],
            inputAudioTranscription: {},
            outputAudioTranscription: {},
          };
          this.session = await this.client.live.connect({
            model: 'gemini-2.5-flash-native-audio',
            config: minimalConfig,
            callbacks,
          });
          this._status = 'open';
          this.emitter.emit('open');
          return true;
        } catch (minimalError) {
          console.error('Minimal config also failed:', minimalError);
          // Fall through to error handling
        }
      }
      
      this._status = 'closed';
      this.session = undefined;
      const errorEvent = new ErrorEvent('error', {
        error: e,
        message: e?.message || 'Failed to connect.',
      });
      this.onError(errorEvent);
      return false;
    }

    this._status = 'open';
    return true;
  }

  public start() {
    console.log('GenAILiveClient.start() called');
    console.log('Status:', this._status);
    
    if (this._status !== 'open') {
      console.error('Client not connected, status:', this._status);
      this.emitter.emit('error', new ErrorEvent('Client is not connected.'));
      return;
    }
    
    console.log('Live session ready for communication');
  }

  public stop() {
    // This method is now primarily for stopping the recorder, which is handled elsewhere.
    // Kept for API compatibility if needed.
  }

  public disconnect() {
    this.session?.close();
    this.session = undefined;
    this._status = 'closed';

    this.log('client.close', `Disconnected`);
    return true;
  }

  private startKeepAlive() {
    // Clear any existing interval
    this.stopKeepAlive();
    
    // Send periodic keep-alive messages every 10 seconds
    this.keepAliveInterval = setInterval(() => {
      if (this._status === 'open' && this.session) {
        try {
          // Send a small amount of silence to keep connection alive
          const silenceData = new ArrayBuffer(320); // 20ms of silence at 16kHz
          const silenceBase64 = btoa(String.fromCharCode(...new Uint8Array(silenceData)));
          
          this.session.sendRealtimeInput({
            media: {
              data: silenceBase64,
              mimeType: 'audio/pcm;rate=16000'
            }
          });
          
          console.log('Keep-alive sent');
        } catch (error) {
          console.log('Keep-alive error:', error);
          this.stopKeepAlive();
        }
      }
    }, 10000); // Send every 10 seconds
  }

  private stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = undefined;
    }
  }

  public send(parts: Part | Part[], turnComplete: boolean = true) {
    if (this._status !== 'open' || !this.session) {
      console.warn('Client is not connected, cannot send data');
      this.emitter.emit('error', new ErrorEvent('Client is not connected'));
      return;
    }
    
    try {
      this.session.sendClientContent({ turns: parts, turnComplete });
      this.log(`client.send`, parts);
    } catch (error) {
      console.error('Error sending data:', error);
      this._status = 'closed';
      this.emitter.emit('error', new ErrorEvent(`Send failed: ${error}`));
    }
  }

  public sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    // Guard against sending data on a non-open socket
    if (this._status !== 'open' || !this.session) {
      // This is the key fix: simply return if the socket is not ready.
      return;
    }
    
    try {
      chunks.forEach(chunk => {
        this.session!.sendRealtimeInput({ media: chunk });
      });

      let hasAudio = false;
      let hasVideo = false;
      for (let i = 0; i < chunks.length; i++) {
        const ch = chunks[i];
        if (ch.mimeType.includes('audio')) hasAudio = true;
        if (ch.mimeType.includes('image')) hasVideo = true;
        if (hasAudio && hasVideo) break;
      }

      let message = 'unknown';
      if (hasAudio && hasVideo) message = 'audio + video';
      else if (hasAudio) message = 'audio';
      else if (hasVideo) message = 'video';
      this.log(`client.realtimeInput`, message);
    } catch (error) {
      console.error('Error sending realtime input:', error);
      this._status = 'closed';
      // Only emit error for actual errors, not normal disconnections
      if (error instanceof Error && !error.message.includes('CLOSING') && !error.message.includes('CLOSED')) {
        this.emitter.emit('error', new ErrorEvent(`Realtime input failed: ${error}`));
      }
    }
  }

  public sendToolResponse(toolResponse: LiveClientToolResponse) {
    if (this._status !== 'open' || !this.session) {
      // FIX: Changed this.emit to this.emitter.emit
      this.emitter.emit('error', new ErrorEvent('Client is not connected'));
      return;
    }
    if (
      toolResponse.functionResponses &&
      toolResponse.functionResponses.length
    ) {
      this.session.sendToolResponse({
        functionResponses: toolResponse.functionResponses!,
      });
    }

    this.log(`client.toolResponse`, { toolResponse });
  }

  // Add recursion guard to prevent infinite loops
  private isProcessingMessage = false;

  protected onMessage(message: LiveServerMessage) {
    if (this.isProcessingMessage) return;
    this.isProcessingMessage = true;
    
    try {
      if (message.setupComplete) {
        // FIX: Changed this.emit to this.emitter.emit
        this.emitter.emit('setupcomplete');
        return;
      }
    if (message.toolCall) {
      this.log('server.toolCall', message);
      // FIX: Changed this.emit to this.emitter.emit
      this.emitter.emit('toolcall', message.toolCall);
      return;
    }
    if (message.toolCallCancellation) {
      this.log('receive.toolCallCancellation', message);
      // FIX: Changed this.emit to this.emitter.emit
      this.emitter.emit('toolcallcancellation', message.toolCallCancellation);
      return;
    }

    if (message.serverContent) {
      const { serverContent } = message;
      if (serverContent.interrupted) {
        this.log('receive.serverContent', 'interrupted');
        // FIX: Changed this.emit to this.emitter.emit
        this.emitter.emit('interrupted');
        return;
      }

      if (serverContent.inputTranscription) {
        // FIX: Changed this.emit to this.emitter.emit
        this.emitter.emit(
          'inputTranscription',
          serverContent.inputTranscription.text,
          // The 'isFinal' property is determined by the presence of 'turnComplete' in the message.
          !!serverContent.turnComplete,
        );
        this.log(
          'server.inputTranscription',
          serverContent.inputTranscription.text,
        );
      }

      if (serverContent.outputTranscription) {
        // FIX: Changed this.emit to this.emitter.emit
        this.emitter.emit(
          'outputTranscription',
          serverContent.outputTranscription.text,
          // The 'isFinal' property is determined by the presence of 'turnComplete' in the message.
          !!serverContent.turnComplete,
        );
        this.log(
          'server.outputTranscription',
          serverContent.outputTranscription.text,
        );
      }

      if (serverContent.modelTurn) {
        let parts: Part[] = serverContent.modelTurn.parts || [];

        const audioParts = parts.filter(p =>
          p.inlineData?.mimeType?.startsWith('audio/pcm'),
        );
        const base64s = audioParts.map(p => p.inlineData?.data);
        const otherParts = difference(parts, audioParts);

        base64s.forEach(b64 => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            // FIX: Changed this.emit to this.emitter.emit
            this.emitter.emit('audio', data);
            this.log(`server.audio`, `buffer (${data.byteLength})`);
          }
        });

        if (otherParts.length > 0) {
          const content: LiveServerContent = { modelTurn: { parts: otherParts } };
          // FIX: Changed this.emit to this.emitter.emit
          this.emitter.emit('content', content);
          this.log(`server.content`, message);
        }
      }

      if (serverContent.turnComplete) {
        this.log('server.send', 'turnComplete');
        // FIX: Changed this.emit to this.emitter.emit
        this.emitter.emit('turncomplete');
      }
    }
    } finally {
      // Reset the flag after a short delay to allow processing
      setTimeout(() => {
        this.isProcessingMessage = false;
      }, 100);
    }
  }

  protected onError(e: ErrorEvent) {
    if (this._status === 'closed') return; // Ignore errors during/after graceful close
    this._status = 'closed';
    console.error('error:', e);

    // Only emit error if it's a real error with a message
    if (e.message && e.message.length > 0) {
      const message = `Could not connect to GenAI Live: ${e.message}`;
      this.log(`server.${e.type}`, message);
      this.emitter.emit('error', e);
    } else {
      console.log('Ignoring generic error event to prevent spam');
    }
  }

  protected onOpen() {
    this._status = 'open';
    console.log('✅ Gemini Live connection opened');
    this.emitter.emit('open');
  }

  protected onClose(e: CloseEvent) {
    if (this._status === 'closed') return; // Prevent duplicate close events
    this._status = 'closed';
    let reason = e.reason || '';
    if (reason.toLowerCase().includes('error')) {
      const prelude = 'ERROR]';
      const preludeIndex = reason.indexOf(prelude);
      if (preludeIndex > 0) {
        reason = reason.slice(preludeIndex + prelude.length + 1, Infinity);
      }
    }

    this.log(
      `server.${e.type}`,
      `disconnected ${reason ? `with reason: ${reason}` : ``}`
    );
    // FIX: Changed this.emit to this.emitter.emit
    this.emitter.emit('close', e);
  }

  /**
   * Internal method to emit a log event.
   * @param type - Log type
   * @param message - Log message
   */
  protected log(type: string, message: string | object) {
    // FIX: Changed this.emit to this.emitter.emit
    this.emitter.emit('log', {
      type,
      message,
      date: new Date(),
    });
  }
}
